import {
  StapleTableHeader,
  StapleTableCell,
  StapleTableRow,
  StapleTableHeading,
  StapleTableBody,
  Tooltip,
} from '@insurancemenu/imenu-components-lib';
import { observer } from 'mobx-react-lite';
import React, { useEffect } from 'react';
import { ActionsUiStores, EmployeeActionUiPresenter } from 'store/modules/add-employees/employeeDelegate';
import { AvailableActions } from 'components/common/add-employees/AvailableActions';
import WarningModals from 'components/common/add-employees/WarningModals';
import { DependentsQuantity } from 'components/common/add-employees/DependentsQuantity';
import { useQuoteAddEmployeesPageStore } from 'store/pages/quote/add-employees/pageStore';
import { QuoteChecklistEmployeeAction } from 'store/pages/quote/add-employees/quoteChecklistEmployeeActions';
import { Person, SupervisorAccount, UserCheck } from '@insurancemenu/imenu-components-lib/dist/icons';
import {
  InterestedProductLine,
  SubscriberPresenter,
  SubscriberProgress,
  SubscribersSortBy,
  SubscribersSortOrder,
  RequiredSubInfoPresenter,
} from 'types/electionService';
import { PRODUCT_LINES_CONTENT_MAP } from '@insurancemenu/imenu-components-lib/dist/utils/ProductLinesHelper';
import SubscriberProgressTag from 'components/common/add-employees/SubscriberProgressTag';
import { checkTotalRateCalculation } from 'global/utils/election/subscriberValidation';
import { AlignmentTypes } from '@insurancemenu/imenu-components-lib/dist/types';
import {
  ActionCellStyled,
  ProductLineItemsListStyled,
  ProductLineItemStyled,
  StapleTableStyled,
  TooltipLabelStyled,
} from './styles';
import { ADD_EMPLOYEES_TABLE_TEST_IDS } from '../constants';

const ProductLineList: React.FC<{
  interestedProductLines: InterestedProductLine[];
  requiredSubInfo: RequiredSubInfoPresenter | null;
  subscriber: SubscriberPresenter;
}> = observer(({ subscriber, interestedProductLines, requiredSubInfo }) => {
  const totalRateCalculationResult = requiredSubInfo
    ? checkTotalRateCalculation({
        subscriber,
        electionFlow: 'quoting',
        requiredSubInfo,
      })
    : {
        incompleteProductLineIds: [],
        productLineIdsMissingSelection: [],
        errorTypes: [],
      };

  const productLinesSorted = interestedProductLines
    .filter((productLine) => productLine.value === 'I')
    .map((productLine) => ({
      ...productLine,
      isValid: !totalRateCalculationResult.incompleteProductLineIds.includes(productLine.productLineId),
    }))
    .sort((a, b) => (b.isValid ? 1 : -1));

  const atLeastOneInvalidProductLine = totalRateCalculationResult.incompleteProductLineIds.length > 0;
  const productLineItemsList = (
    <ProductLineItemsListStyled>
      {productLinesSorted.map((productLine) => (
        <ProductLineItemStyled key={productLine.productLineId} $isValid={productLine.isValid}>
          {PRODUCT_LINES_CONTENT_MAP[productLine.productLineId]?.label}
        </ProductLineItemStyled>
      ))}
    </ProductLineItemsListStyled>
  );
  return atLeastOneInvalidProductLine ? (
    <Tooltip
      alignment={AlignmentTypes.BOTTOM_START}
      label={
        <TooltipLabelStyled>
          Required data is missing for highlighted product lines. Complete the missing information in &apos;Manage
          Elections&apos;.
        </TooltipLabelStyled>
      }
    >
      {productLineItemsList}
    </Tooltip>
  ) : (
    productLineItemsList
  );
});

interface EmployeeEntry {
  name: string;
  progress: SubscriberProgress;
  subscriber: SubscriberPresenter;
  dependentsNumber: number;
  actions: EmployeeActionUiPresenter[];
  actionsUiStores: ActionsUiStores;
  interestedProductLines: InterestedProductLine[];
  requiredSubInfo: RequiredSubInfoPresenter | null;
}

export const quoteChecklistEmployeeActionIcons: Partial<Record<string, React.FC>> = {
  [QuoteChecklistEmployeeAction.QUOTE_CHECKLIST_EMPLOYEE_PROFILE]: Person,
  [QuoteChecklistEmployeeAction.QUOTE_CHECKLIST_MANAGE_DEPENDENTS]: SupervisorAccount,
  [QuoteChecklistEmployeeAction.QUOTE_CHECKLIST_INTERESTED_PRODUCT_LINES]: UserCheck,
};

const QuoteAddEmployeesTableRow: React.FC<EmployeeEntry> = observer(
  ({
    name,
    progress,
    dependentsNumber,
    subscriber,
    requiredSubInfo,
    actions,
    actionsUiStores,
    interestedProductLines,
  }) => {
    return (
      <StapleTableRow data-test-id={ADD_EMPLOYEES_TABLE_TEST_IDS.TABLE_ROW}>
        <StapleTableCell width="6rem">
          {name}
          <DependentsQuantity dependentsNumber={dependentsNumber} />
        </StapleTableCell>
        <StapleTableCell width="6rem">
          <SubscriberProgressTag status={progress} />
        </StapleTableCell>
        <StapleTableCell width="15rem">
          <ProductLineList
            subscriber={subscriber}
            interestedProductLines={interestedProductLines}
            requiredSubInfo={requiredSubInfo}
          />
        </StapleTableCell>
        <ActionCellStyled width="5rem">
          <AvailableActions
            availableActions={actions}
            warningModals={actionsUiStores.warningModalDelegates}
            actionIcons={quoteChecklistEmployeeActionIcons}
          />
          <WarningModals availableActions={actions} warningModals={actionsUiStores.warningModalDelegates} />
        </ActionCellStyled>
      </StapleTableRow>
    );
  },
);

export const QuoteAddEmployeesTable: React.FC = observer(() => {
  const { getEmployees, getRequiredSubInfo, paginationDelegate, employees, isLoading, requiredSubInfoPresenter } =
    useQuoteAddEmployeesPageStore();

  const onEmployeeNameSort = (sortKey: string, sortingOrder: 'ASC' | 'DESC'): void => {
    paginationDelegate.setSortBy(sortKey as SubscribersSortBy);
    paginationDelegate.setSortOrder(sortingOrder as SubscribersSortOrder);
    paginationDelegate.setPage(1);
  };

  useEffect(() => {
    void getRequiredSubInfo();
  }, []);

  useEffect(() => {
    void getEmployees();
  }, [
    paginationDelegate.page,
    paginationDelegate.sortBy,
    paginationDelegate.sortOrder,
    paginationDelegate.filters.employeeName,
    paginationDelegate.filters.subProgress,
  ]);

  return (
    <StapleTableStyled
      data-test-id={ADD_EMPLOYEES_TABLE_TEST_IDS.TABLE}
      totalElements={paginationDelegate.totalElements}
      loading={isLoading}
      pagination={{
        tablePage: paginationDelegate.page,
        minPage: 1,
        maxPage: paginationDelegate.totalPages,
        onPageChange: (page) => {
          paginationDelegate.setPage(page);
        },
      }}
    >
      <StapleTableHeader>
        <StapleTableRow>
          <StapleTableHeading
            width="6rem"
            sorting
            sortingKey={SubscribersSortBy.NAME}
            onSortCallback={onEmployeeNameSort}
          >
            Employee Name
          </StapleTableHeading>
          <StapleTableHeading width="6rem">Progress</StapleTableHeading>
          <StapleTableHeading width="15rem">Interested Product Lines</StapleTableHeading>
          <StapleTableHeading width="5rem" />
        </StapleTableRow>
      </StapleTableHeader>
      <StapleTableBody>
        {employees.map(({ rawData, fullName, dependentsNumber, availableActions, actionsUiStores }) => (
          <QuoteAddEmployeesTableRow
            key={rawData.id}
            name={fullName}
            progress={rawData.progress}
            dependentsNumber={dependentsNumber}
            actions={availableActions}
            actionsUiStores={actionsUiStores}
            interestedProductLines={rawData.interestedProductLines}
            subscriber={rawData}
            requiredSubInfo={requiredSubInfoPresenter}
          />
        ))}
      </StapleTableBody>
    </StapleTableStyled>
  );
});
